import { sql } from 'drizzle-orm'
import { boolean, check, index, pgTable, text, timestamp } from 'drizzle-orm/pg-core'

import { user } from './auth.ts'
import { createdAt, primaryId, updatedAt } from './columns.ts'

/** The two kinds of entry, and the column's whole domain. */
export const CALENDAR_ENTRY_KINDS = ['PRIVATE', 'BUSINESS'] as const
export type CalendarEntryKind = (typeof CALENDAR_ENTRY_KINDS)[number]

/** Longest title the column accepts. A title is a line on a calendar block. */
export const CALENDAR_ENTRY_TITLE_MAX = 200
/** Longest note the column accepts — a paragraph, not a pasted document. */
export const CALENDAR_ENTRY_NOTES_MAX = 2000

/**
 * One line somebody wrote into their own calendar — „dentysta", „spotkanie z
 * dostawcą", „urlop".
 *
 * Owner: „admini mogą wpisywać w nich swoje prywatne spotkania, spotkania
 * biznesowe etc […] ma działać trochę jak kalendarz Google". So it is a diary
 * row and nothing more: no offering, no customer, no price, no status. That is
 * exactly why it could not be a `booking`. A booking needs an offering, carries
 * customer fields, and the panel's create path writes a confirmation email to
 * whoever is on it.
 *
 * ── IT IS BUSY TIME, NOT A SECOND AVAILABILITY MODEL ────────────────────
 * The fifth thing standing in somebody's week, next to the four listed on
 * `performer_availability`, and deliberately not a rival to any of them. Its
 * rows reach the ONE conflict rule through the same two timeline readers every
 * reservation does (`listPerformerTimeline`, `listTimelinesFor`), as spans with
 * `source: 'ENTRY'`. There is no separate "is the diary free?" check anywhere,
 * so a private entry blocks its author for a shoot, a reservation and a
 * meeting alike, with the same answer.
 *
 * ── WHO SEES WHAT ────────────────────────────────────────────────────────
 * The owner, the title and the notes. Everybody else, a block marked „Zajęty"
 * with the person's first name (owner: „inni widzą to jako zajęty, ale ty —
 * właściciel konta — widzisz szczegóły i opis"). That masking happens in
 * `@repo/core`, and the timeline readers never carry the title at all: an
 * entry span is titled „Zajęty termin" at the query, so a private line cannot
 * leak through a clash message on somebody else's screen.
 *
 * Two instants rather than a day plus clock readings, for the reason
 * `performer_availability` gives: every overlap test in this schema compares
 * instants. `is_all_day` is for DRAWING only; the span still has to cover the
 * day it claims, and the conflict test reads the span.
 */
export const calendarEntry = pgTable(
  'calendar_entry',
  {
    id: primaryId('cent'),

    /**
     * Whose calendar this is written in, and the only person who will ever read
     * its title.
     *
     * `CASCADE` for an account that is truly deleted. An account that is CLOSED
     * is anonymised instead and its row survives, so the cascade never fires
     * there — `closeUserAccount` deletes these rows by hand, and
     * `.agents/checks.sh` makes sure it keeps doing so.
     */
    userId: text('user_id')
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),

    /** `PRIVATE` or `BUSINESS`. For the owner's own colour, never for anyone else. */
    kind: text('kind').$type<CalendarEntryKind>().notNull(),

    title: text('title').notNull(),
    notes: text('notes'),

    startsAt: timestamp('starts_at', { withTimezone: true }).notNull(),
    endsAt: timestamp('ends_at', { withTimezone: true }).notNull(),
    isAllDay: boolean('is_all_day').notNull().default(false),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    // THE read: "what is in this person's week?", for the calendar and for
    // every conflict check on them.
    index('calendar_entry_user_idx').on(t.userId, t.startsAt),
    // Two kinds and nothing else, for the reason `booking_meeting_place_known`
    // gives: the calendar's colour rule reads this column, and a third spelling
    // would fall through it silently.
    check('calendar_entry_kind_known', sql`${t.kind} IN ('PRIVATE', 'BUSINESS')`),
    // Strict `>` with a half-open period, as on `booking`: a zero-length entry
    // blocks nothing and overlaps nothing, which is a control used wrongly.
    check('calendar_entry_period_ordered', sql`${t.endsAt} > ${t.startsAt}`),
    // A block on the grid needs a word on it, and a runaway paste is not one.
    check(
      'calendar_entry_title_length',
      sql`length(btrim(${t.title})) BETWEEN 1 AND ${sql.raw(String(CALENDAR_ENTRY_TITLE_MAX))}`,
    ),
    check(
      'calendar_entry_notes_length',
      sql`${t.notes} IS NULL OR length(${t.notes}) <= ${sql.raw(String(CALENDAR_ENTRY_NOTES_MAX))}`,
    ),
  ],
)

export type CalendarEntry = typeof calendarEntry.$inferSelect
export type NewCalendarEntry = typeof calendarEntry.$inferInsert
